import React from "react";
import {
  Box,
  IconButton,
  Typography,
  Tooltip
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams
} from "@mui/x-data-grid";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAddOutlined
} from "@mui/icons-material";
import { ManagerData } from "../../core/api/manager/ManagersModel";
import { translations } from "../../translations/common";

interface ManagersTableProps {
  managers: ManagerData[];
  onEdit: (manager: ManagerData) => void;
  onDelete: (manager: ManagerData) => void;
  onAddNew: () => void;
}

export const ManagersTable: React.FC<ManagersTableProps> = ({
  managers,
  onEdit,
  onDelete,
  onAddNew
}) => {
  const columns: GridColDef[] = [
    {
      field: "firstName",
      headerName: translations.managers.columns.firstName,
      flex: 1
    },
    {
      field: "lastName",
      headerName: translations.managers.columns.lastName,
      flex: 1
    },
    {
      field: "email",
      headerName: translations.managers.columns.email,
      flex: 1
    },
    {
      field: "auth0Email",
      headerName: translations.managers.columns.authEmail,
      flex: 1
    },
    {
      field: "userType",
      headerName: translations.managers.columns.userType,
      flex: 1,
      renderCell: (params: GridRenderCellParams<ManagerData>) => {
        const userType = params.value as keyof typeof translations.managers.userTypes;
        return translations.managers.userTypes[userType] || params.value;
      }
    },
    {
      field: "active",
      headerName: translations.managers.columns.status,
      flex: 1,
      renderCell: (params: GridRenderCellParams<ManagerData>) => {
        return params.value === true
          ? translations.managers.status.active
          : translations.managers.status.inactive;
      }
    },
    {
      field: "actions",
      headerName: "",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ManagerData>) => {
        const manager = params.row;
        return (
          <Box sx={{ display: "flex" }}>
            <Tooltip title={translations.managers.actions.edit}>
              <IconButton
                onClick={() => onEdit(manager)}
                size="small"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={translations.managers.actions.delete}>
              <IconButton
                onClick={() => onDelete(manager)}
                size="small"
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{
      width: "100%",
      height: '100%',
      bgcolor: 'background.paper',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          fontWeight: 'bold',
          fontSize: '1.1rem',
          borderBottom: '1px solid #ddd'
        }}
      >
        <Typography variant="h6">{translations.managers.title}</Typography>
        <IconButton
          onClick={onAddNew}
          color="primary"
          size="small"
        >
          <PersonAddOutlined />
        </IconButton>
      </Box>
      <DataGrid
        rows={managers}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
        pageSizeOptions={[10]}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={(row) => row.id || row.username}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
        }
        sx={{
          border: 'none',
          '& .even-row': {
            backgroundColor: '#ffffff',
          },
          '& .odd-row': {
            backgroundColor: '#efefef',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#bdbdbd',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          '& .MuiDataGrid-row': {
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
        }}
      />
    </Box>
  );
};

export default ManagersTable;
